import { useEffect, useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, RotateCcw } from "lucide-react";
import { fetchStudyDeck, setProgress } from "@/lib/api";
import { loadStudySession, saveStudySession, clearStudySession } from "@/lib/studySession";
import JlptBadge from "@/components/JlptBadge";

// Hoisted motion variants — declared once instead of recreated every render.
const CARD_TRANSITION = { duration: 0.2 };
const CARD_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
};
const ANSWER_VARIANTS = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
};
const PROGRESS_TRANSITION = { duration: 0.3 };

/**
 * Study mode — flashcards from a queue of the user's `learning` kanji plus
 * easy `new` ones. For each card:
 *   1. Show the kanji + JLPT.
 *   2. User taps "Reveal" to see meaning + readings.
 *   3. User marks "Still learning" or "Got it" (which writes /api/progress).
 */
export default function Study() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const deckQ = useQuery({
    queryKey: ["study-deck"],
    queryFn: () => fetchStudyDeck(20),
    refetchOnWindowFocus: false,
  });
  // Restore an in-progress session (e.g. user clicked a kanji to
  // investigate its card mid-deck, then came back).
  const [saved] = useState(() => loadStudySession());
  const [restoredItems, setRestoredItems] = useState(saved?.items?.length ? saved.items : null);
  const [idx, setIdx] = useState(saved?.idx || 0);
  const [revealed, setRevealed] = useState(saved?.revealed || false);
  const [reviewed, setReviewed] = useState(saved?.reviewed || 0);

  const items = restoredItems || deckQ.data?.items || [];
  const counts = deckQ.data?.counts || { learning: 0, new: 0, known: 0 };
  const current = items[idx];

  const mark = useMutation({
    mutationFn: ({ ch, status }) => setProgress(ch, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["progress"] }),
  });

  const next = (status) => {
    if (!current) return;
    mark.mutate({ ch: current.kanji, status });
    setReviewed((r) => r + 1);
    setRevealed(false);
    setIdx((i) => i + 1);
  };

  const restart = () => {
    clearStudySession();
    setRestoredItems(null);
    setIdx(0);
    setRevealed(false);
    setReviewed(0);
    qc.invalidateQueries({ queryKey: ["study-deck"] });
    deckQ.refetch();
  };

  const total = items.length;
  const done = idx >= total;

  // Persist "where you left off" on every change so any navigation
  // away (kanji card, search, journal…) can resume seamlessly.
  useEffect(() => {
    if (!total) return;
    if (idx >= total) { clearStudySession(); return; }
    saveStudySession({ items, idx, revealed, reviewed });
  }, [items, idx, revealed, reviewed, total]);

  const stats = useMemo(() => (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] font-sans text-sumiSoft">
      <span>{counts.known} known</span>
      <span className="opacity-50">·</span>
      <span>{counts.learning} learning</span>
    </div>
  ), [counts]);

  if (deckQ.isLoading && !restoredItems) {
    return (
      <div className="p-8 text-center" data-testid="study-page">
        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumiSoft animate-pulse">
          loading deck…
        </p>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="study-page">
        <h2 className="font-serif text-3xl mb-2">Study</h2>
        <p className="text-sumiSoft font-sans text-sm mb-6">
          Nothing in the queue yet. Pick a kanji from the Build path and mark it{" "}
          <span className="text-amber-600">Learning</span> to start a deck.
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 border border-sumi text-xs uppercase tracking-[0.25em] font-sans hover:bg-sumi hover:text-washi transition-colors"
          data-testid="study-empty-to-build"
        >
          go to build path
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="study-page">
        <h2 className="font-serif text-3xl mb-2">Session done</h2>
        <p className="text-sumiSoft font-sans text-sm mb-1">
          You reviewed {reviewed} of {total} kanji.
        </p>
        {stats}
        <div className="flex items-center gap-3 justify-center mt-8">
          <button
            onClick={restart}
            data-testid="study-restart"
            className="px-4 py-2 border border-sumi text-xs uppercase tracking-[0.25em] font-sans hover:bg-sumi hover:text-washi transition-colors flex items-center gap-2"
          >
            <RotateCcw size={14} /> new deck
          </button>
          <button
            onClick={() => navigate("/journal")}
            data-testid="study-to-journal"
            className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-sans text-sumi hover:text-shu transition-colors"
          >
            view journal →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-3 md:p-8" data-testid="study-page">
      {/* progress strip */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-2xl md:text-3xl">Study</h2>
        <div className="flex items-center gap-3">
          <p className="font-sans text-[10px] md:text-xs uppercase tracking-[0.25em] text-sumiSoft" data-testid="study-progress">
            {idx + 1} / {total}
          </p>
          <button
            onClick={restart}
            title="Start a fresh deck"
            data-testid="study-new-deck"
            className="text-sumiSoft hover:text-shu transition-colors"
          >
            <RotateCcw size={14} />
          </button>
        </div>
      </div>
      <div className="h-1 bg-borderDefault mb-6 overflow-hidden">
        <motion.div
          className="h-full bg-moss"
          style={{ background: "#6E7A5A" }}
          animate={{ width: `${(idx / total) * 100}%` }}
          transition={PROGRESS_TRANSITION}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.kanji + "-" + idx}
          {...CARD_VARIANTS}
          transition={CARD_TRANSITION}
          className="bg-washiPanel border border-borderDefault px-6 py-10 flex flex-col items-center"
          data-testid="study-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <span
              className="text-[9px] uppercase tracking-[0.3em] font-sans px-2 py-0.5 border"
              style={{
                borderColor: current.status === "learning" ? "#D29C4C" : "#7A746D",
                color: current.status === "learning" ? "#D29C4C" : "#7A746D",
              }}
              data-testid="study-card-status"
            >
              {current.status}
            </span>
            <JlptBadge level={current.jlpt} size="sm" />
          </div>

          <button
            type="button"
            onClick={() => navigate(`/kanji/${encodeURIComponent(current.kanji)}`)}
            title="Open this kanji's card — your session is saved"
            data-testid="study-kanji-link"
            className="group"
          >
            <span
              className="font-serif text-[7rem] md:text-[9rem] leading-none text-sumi group-hover:text-shu transition-colors"
              data-testid="study-card-kanji"
            >
              {current.kanji}
            </span>
          </button>
          <p className="font-sans text-[9px] uppercase tracking-[0.3em] text-sumiSoft mt-3">
            tap the kanji to explore its card
          </p>

          {!revealed ? (
            <button
              onClick={() => setRevealed(true)}
              data-testid="study-reveal"
              className="mt-8 inline-flex items-center gap-2 px-4 py-2 border border-sumi text-xs uppercase tracking-[0.25em] font-sans hover:bg-sumi hover:text-washi transition-colors"
            >
              <Eye size={14} /> reveal
            </button>
          ) : (
            <motion.div
              {...ANSWER_VARIANTS}
              className="mt-6 flex flex-col items-center w-full"
              data-testid="study-card-answer"
            >
              <p className="font-serif text-2xl md:text-3xl text-sumi text-center">
                {current.meaning}
              </p>
              {current.meanings?.length > 1 && (
                <p className="font-sans text-xs text-sumiSoft text-center tracking-wide mt-1 max-w-[40ch]">
                  {current.meanings.slice(1).join(" · ")}
                </p>
              )}
              <div className="grid grid-cols-2 gap-px bg-borderDefault mt-5 w-full max-w-md">
                <div className="bg-washi px-3 py-2 text-center">
                  <p className="text-[9px] uppercase tracking-[0.3em] text-sumiSoft font-sans">on&apos;yomi</p>
                  <p className="font-serif text-lg text-sumi mt-1">
                    {current.on_readings?.length ? current.on_readings.join("、") : "—"}
                  </p>
                </div>
                <div className="bg-washi px-3 py-2 text-center">
                  <p className="text-[9px] uppercase tracking-[0.3em] text-sumiSoft font-sans">kun&apos;yomi</p>
                  <p className="font-serif text-lg text-sumi mt-1">
                    {current.kun_readings?.length ? current.kun_readings.join("、") : "—"}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-7 w-full max-w-md">
                <button
                  onClick={() => next("learning")}
                  data-testid="study-mark-learning"
                  className="flex-1 py-2.5 text-[11px] uppercase tracking-[0.25em] font-sans border border-borderDefault text-sumi hover:border-amber-500 hover:text-amber-700 transition-colors"
                >
                  still learning
                </button>
                <button
                  onClick={() => next("known")}
                  data-testid="study-mark-known"
                  className="flex-1 py-2.5 text-[11px] uppercase tracking-[0.25em] font-sans text-washi transition-opacity hover:opacity-90"
                  style={{ background: "#6E7A5A" }}
                >
                  got it
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex justify-center">
        {stats}
      </div>
    </div>
  );
}
